import React, { useCallback, useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Stack,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { getIframeVisibleCenter } from '../helpers'

function parseCsvLine(line: string): string[] {
  const out: string[] = []
  let i = 0
  while (i < line.length) {
    if (line[i] === '"') {
      i += 1
      let end = i
      while (end < line.length && line[end] !== '"') {
        if (line[end] === '\\') end += 1
        end += 1
      }
      out.push(line.slice(i, end).replace(/""/g, '"'))
      i = end + 1
      if (line[i] === ',') i += 1
    } else {
      let end = line.indexOf(',', i)
      if (end === -1) end = line.length
      out.push(line.slice(i, end).trim())
      i = end + 1
    }
  }
  return out
}

function parseTeachersCsv(
  text: string
): { name: string[]; email: string[] } | string {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
  if (lines.length < 2)
    return 'CSV must have a header row and at least one data row'
  const headerRow = parseCsvLine(lines[0])
  const headers = headerRow.map((h) => h.toLowerCase().replace(/\s|-|'/g, ''))
  const nameIdx = headers.findIndex((h) =>
    ['name', 'teachername', 'fullname'].includes(h)
  )
  const emailIdx = headers.findIndex((h) =>
    ['email', 'emailaddress'].includes(h)
  )
  if (nameIdx === -1 || emailIdx === -1) {
    return 'CSV must contain columns for name (or "teacher name", "full name") and email (or "email address")'
  }
  const name: string[] = []
  const email: string[] = []
  const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  for (let r = 1; r < lines.length; r += 1) {
    const row = parseCsvLine(lines[r])
    const n = (row[nameIdx] || '').trim()
    const e = (row[emailIdx] || '').trim().toLowerCase()
    // eslint-disable-next-line no-continue
    if (!e) continue
    // eslint-disable-next-line no-continue
    if (!emailRe.test(e)) continue
    name.push(n || e)
    email.push(e)
  }
  if (name.length === 0) return 'No valid rows with email addresses found'
  return { name, email }
}

interface ImportTeachersModalProps {
  open: boolean
  onClose: () => void
  onImport: (name: string[], email: string[]) => Promise<void>
  disabled?: boolean
}

export default function ImportTeachersModal({
  open,
  onClose,
  onImport,
  disabled = false,
}: ImportTeachersModalProps) {
  const [center, setCenter] = useState(0)
  useEffect(() => {
    if (open) setCenter(getIframeVisibleCenter())
  }, [open])
  const [dragOver, setDragOver] = useState(false)
  const [parseError, setParseError] = useState<string | null>(null)
  const [parsed, setParsed] = useState<{
    name: string[]
    email: string[]
  } | null>(null)
  const [uploading, setUploading] = useState(false)

  const handleClose = useCallback(() => {
    setParsed(null)
    setParseError(null)
    onClose()
  }, [onClose])

  const handleFile = useCallback(async (file: File) => {
    setParseError(null)
    setParsed(null)
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setParseError('Please upload a CSV file')
      return
    }
    const text = await file.text()
    const result = parseTeachersCsv(text)
    if (typeof result === 'string') {
      setParseError(result)
      return
    }
    setParsed(result)
  }, [])

  const handleSubmit = useCallback(async () => {
    if (!parsed) return
    setUploading(true)
    setParseError(null)
    try {
      await onImport(parsed.name, parsed.email)
      handleClose()
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error ||
        (err as { response?: { data?: string } })?.response?.data ||
        (err as Error).message ||
        'Import failed'
      setParseError(typeof msg === 'string' ? msg : 'Import failed')
    } finally {
      setUploading(false)
    }
  }, [parsed, onImport, handleClose])

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setDragOver(false)
      const file = e.dataTransfer.files[0]
      if (file) handleFile(file)
    },
    [handleFile]
  )

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(true)
  }, [])

  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
  }, [])

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) handleFile(file)
      e.target.value = ''
    },
    [handleFile]
  )

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
      PaperProps={{
        sx: {
          marginTop: `${center}px`,
          transform: 'translateY(-50%)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        Import teachers
        <IconButton aria-label='Close' onClick={handleClose} size='small'>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {parsed ? (
          <>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              {parsed.name.length} teacher{parsed.name.length !== 1 ? 's' : ''}{' '}
              ready to import.
            </Typography>
            {parseError && (
              <Typography variant='body2' color='error' sx={{ mb: 2 }}>
                {parseError}
              </Typography>
            )}
          </>
        ) : (
          <>
            <Box
              onDrop={onDrop}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              sx={{
                border: '2px dashed',
                borderColor: dragOver ? 'primary.main' : 'grey.300',
                borderRadius: 2,
                bgcolor: dragOver ? 'action.hover' : 'grey.50',
                py: 4,
                px: 2,
                textAlign: 'center',
              }}
            >
              <CloudUploadIcon
                sx={{ fontSize: 48, color: 'grey.400', mb: 1 }}
              />
              <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                Drag and drop a CSV file here, or click to choose a file.
              </Typography>
              <Typography
                variant='caption'
                color='text.secondary'
                display='block'
                sx={{ mb: 2 }}
              >
                CSV must have columns for name and email.
              </Typography>
              <Button variant='outlined' component='label' disabled={disabled}>
                Choose file
                <input
                  type='file'
                  accept='.csv,text/csv'
                  hidden
                  onChange={onInputChange}
                />
              </Button>
            </Box>
            {parseError && (
              <Typography variant='body2' color='error' sx={{ mt: 2 }}>
                {parseError}
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 0 }}>
        <Stack direction='row' spacing={2} sx={{ width: '100%' }}>
          {parsed ? (
            <Button
              onClick={() => {
                setParsed(null)
                setParseError(null)
              }}
              disabled={uploading}
            >
              Choose different file
            </Button>
          ) : null}
          <Box sx={{ flex: 1 }} />
          <Button onClick={handleClose} disabled={uploading}>
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={handleSubmit}
            disabled={!parsed || disabled || uploading}
          >
            {uploading ? 'Importing…' : 'Import'}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  )
}

ImportTeachersModal.defaultProps = {
  disabled: false,
}
